<template>
  <form @submit.prevent="submit" autocomplete="off">
    <FormInput
      name="firstName"
      v-model="fields.firstName"
      testId="firstName-input"
      :label="`${$t('global.first-name')}`"
      :errors="validation.fields.firstName.errors"
      :dirty="validation.fields.firstName.dirty"
      :disabled="loading"
    />
    <FormInput
      name="lastName"
      v-model="fields.lastName"
      testId="lastName-input"
      :label="`${$t('global.last-name')}`"
      :errors="validation.fields.lastName.errors"
      :dirty="validation.fields.lastName.dirty"
      :disabled="loading"
    />

    <FormInput
      name="cell"
      type="tel"
      v-model="fields.cell"
      testId="cell-input"
      :label="`${$t('global.mobile-phone')}`"
      :errors="validation.fields.cell.errors"
      :dirty="validation.fields.cell.dirty"
      :disabled="loading"
      :prepend="countryDial"
    />
    <FormSelect2
      :options="countries"
      name="countryCode"
      v-model="fields.countryCode"
      testId="countryCode-input"
      :label="`${$t('global.country-code')}`"
      :errors="validation.fields.countryCode.errors"
      :dirty="validation.fields.countryCode.dirty"
      :disabled="loadingCountries"
      :noneLabel="`- ${$t('global.select-country')} -`"
    />
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="loading || loadingCountries"
        @click="cancel"
        data-test="cancel-pin-edit-button"
        v-if="isCancellable"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="loading || loadingCountries"
        :loading="loading"
        data-test="edit-pin-button"
      >
        {{ $t("global.update-info") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import FormSelect2 from "@/components/display/form/FormSelect2";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { userInfo as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "UserPhoneForm",
  components: {
    FormInput,
    FormSelect2,
    Btn,
  },
  setup() {
    const loading = ref(false);
    const loadingCountries = ref(true);
    const countries = ref([]);
    const errors = ref(null);
    const user = store.state.auth.user;
    const fields = reactive({
      cell: user.cell,
      countryCode: user.countryCode,
      firstName: user.firstName,
      lastName: user.lastName,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        cell: {
          phone: "Must be a valid phone number.",
        },
        countryCode: {
          requiredIf: "Country code is required if adding a phone number.",
        },
      }
    );

    const isCancellable = computed(() => {
      return (
        user.cell !== fields.cell ||
        user.countryCode !== fields.countryCode ||
        user.firstName !== fields.firstName ||
        user.lastName !== fields.lastName
      );
    });

    const cancel = () => {
      fields.firstName = user.firstName;
      fields.lastName = user.lastName;

      fields.cell = user.cell;
      fields.countryCode = user.countryCode;
    };

    const submit = () => {
      if (!isCancellable.value) {
        return;
      }

      errors.value = null;

      validation
        .validate()
        .then(() => {
          loading.value = true;
          store
            .dispatch("auth/editUser", {
              firstName: fields.firstName,
              lastName: fields.lastName,

              cell: fields.cell,
              countryCode: fields.countryCode,
            })
            .then(() => {
              user.firstName = fields.firstName;
              user.lastName = fields.lastName;
              user.cell = fields.cell;
              user.countryCode = fields.countryCode;
              store.dispatch("toasts/addToast", {
                variant: "success",
                message: "Successfully updated your information",
              });
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: "Error saving your information",
              });
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {});
    };

    onMounted(() => {
      loadingCountries.value = true;
      store
        .dispatch("auth/getSupportedCountries")
        .then((data) => {
          countries.value = data;
        })
        .finally(() => {
          loadingCountries.value = false;
        });
    });

    const countryDial = computed(() => {
      // if (!countries.value || !fields.countryCode || loadingCountries.value) {
      //   return null;
      // }

      const foundCountry = countries.value?.find((c) => c.value === fields.countryCode);

      if (foundCountry) {
        return `+${foundCountry.data.dial}`;
      }

      return null;
    });

    return {
      errors,
      fields,
      loading,
      submit,
      validation,
      cancel,
      isCancellable,
      countries,
      countryDial,
      loadingCountries,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
